import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";

// Every route except the homepage is code-split so the first paint only
// downloads the JavaScript the landing page actually needs.
const Rooms = lazy(() => import("./pages/Rooms.tsx"));
const CulinarySafari = lazy(() => import("./pages/CulinarySafari.tsx"));
const AfricanSpa = lazy(() => import("./pages/AfricanSpa.tsx"));
const Experiences = lazy(() => import("./pages/Experiences.tsx"));
const LocationPage = lazy(() => import("./pages/Location.tsx"));
const Gallery = lazy(() => import("./pages/Gallery.tsx"));
const SpecialOffers = lazy(() => import("./pages/SpecialOffers.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const ChildPolicy = lazy(() => import("./pages/ChildPolicy.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const Disclaimer = lazy(() => import("./pages/Disclaimer.tsx"));
const Enquire = lazy(() => import("./pages/Enquire.tsx"));
const RoomPresidential = lazy(() => import("./pages/RoomPresidential.tsx"));
const RoomExecutive = lazy(() => import("./pages/RoomExecutive.tsx"));
const RoomSafari = lazy(() => import("./pages/RoomSafari.tsx"));
const RoomCheetah = lazy(() => import("./pages/RoomCheetah.tsx"));
const RoomElephant = lazy(() => import("./pages/RoomElephant.tsx"));
const RoomRhino = lazy(() => import("./pages/RoomRhino.tsx"));
const Journal = lazy(() => import("./pages/Journal.tsx"));
const JournalArticle = lazy(() => import("./pages/JournalArticle.tsx"));
const MeetingsEvents = lazy(() => import("./pages/MeetingsEvents.tsx"));
const DnsCheck = lazy(() => import("./pages/DnsCheck.tsx"));
const SafariHoneymoons = lazy(() => import("./pages/SafariHoneymoons.tsx"));
const Presentation = lazy(() => import("./pages/Presentation.tsx"));

import SeoDebugPanel from "./components/SeoDebugPanel.tsx";
import PixelTracker from "./components/PixelTracker.tsx";
import ContentProtection from "./components/ContentProtection.tsx";
import AnalyticsTracker from "./components/AnalyticsTracker.tsx";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ContentProtection />
        <SeoDebugPanel />
        <PixelTracker />
        <AnalyticsTracker />
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/rooms" element={<Rooms />} />
            <Route path="/rooms/presidential" element={<RoomPresidential />} />
            <Route path="/rooms/executive" element={<RoomExecutive />} />
            <Route path="/rooms/safari" element={<RoomSafari />} />
            <Route path="/rooms/cheetah" element={<RoomCheetah />} />
            <Route path="/rooms/elephant" element={<RoomElephant />} />
            <Route path="/rooms/rhino" element={<RoomRhino />} />
            <Route path="/culinary" element={<CulinarySafari />} />
            <Route path="/spa" element={<AfricanSpa />} />
            <Route path="/experiences" element={<Experiences />} />
            <Route path="/offers" element={<SpecialOffers />} />
            <Route path="/special-offers" element={<Navigate to="/offers" replace />} />
            <Route path="/safari-honeymoons" element={<SafariHoneymoons />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/location" element={<LocationPage />} />
            <Route path="/events" element={<MeetingsEvents />} />
            <Route path="/investment" element={<Navigate to="/" replace />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/journal/:slug" element={<JournalArticle />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/child-policy" element={<ChildPolicy />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/enquire" element={<Enquire />} />
            <Route path="/dns-check" element={<DnsCheck />} />
            <Route path="/presentation" element={<Presentation />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
